import React, { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import {Link, useNavigate, useParams} from "react-router-dom";
import styled from "styled-components";
import { Formik } from "formik";
import * as Yup from "yup";
import { createForBusinessCard, getForBusinessCard, updateForBusinessCard } from "../../../api/axios";
import CmsCircularProgress from "../../Common/CircularProgress";
import removeIcon from "../../../Assets/remove-image-photo.svg";
import { showToast } from "../../../Utills/toast";
import FormGroup from "../../Common/FormGroup";

const validationSchema = Yup.object(
  Array.from({ length: 8 }, (_, i) => i + 1).reduce((acc, num) => {
    acc[`card_image${num}`] = Yup.mixed().test("required", `Card ${num} image is required`, function (value) {
      const { [`old_card_image${num}`]: oldImage } = this.parent;
      return !!(value || oldImage);
    });
    acc[`content${num}`] = Yup.string().required(`Card ${num} content is required`);
    return acc;
  }, {})
);

const CardSection = ({ cardNum, values, errors, touched, setFieldValue, inputRef, getFieldProps }) => (
  <>
    <SubTitle className={cardNum > 1 ? "mt-3" : ""}>Card {cardNum}</SubTitle>
    <FormGroup
      label={`Select a new Image for Card ${cardNum}`}
      requiredField
      error={touched[`card_image${cardNum}`] && errors[`card_image${cardNum}`] ? errors[`card_image${cardNum}`] : ''}
    >
      <Form.Control
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
        onChange={(e) => setFieldValue(`card_image${cardNum}`, e.target.files[0])}
        style={{ display: "none" }}
        ref={inputRef}
        name={`card_image${cardNum}`}
        isInvalid={touched[`card_image${cardNum}`] && !!errors[`card_image${cardNum}`]}
      />
      {values[`card_image${cardNum}`] ? (
        <ImageWrapper>
          <UploadImage>
            <BootstrapImage
              src={values[`card_image${cardNum}`] instanceof File ? URL.createObjectURL(values[`card_image${cardNum}`]) : values[`card_image${cardNum}`]}
              alt={`card_image${cardNum}`}
              onClick={() => inputRef.current.click()}
            />
          </UploadImage>
          {(values[`card_image${cardNum}`] instanceof File || (values[`card_image${cardNum}`] && values[`card_image${cardNum}`] !== values[`old_card_image${cardNum}`])) && (
            <RemoveButtonWrapper>
              <BootstrapButton 
                type="button"
                onClick={() => {
                  setFieldValue(`card_image${cardNum}`, values[`old_card_image${cardNum}`] || null);
                  if (inputRef.current) inputRef.current.value = '';
                }}
              >
                <img src={removeIcon} alt="removeImage" height={'20px'} width={'20px'}/>
              </BootstrapButton>
            </RemoveButtonWrapper>
          )}
        </ImageWrapper>
      ) : (
        <Button className="mb-0" onClick={() => inputRef.current.click()}>
          Upload the image
        </Button>
      )}
    </FormGroup>
    <br />
    <FormGroup 
      label='Enter a new content' 
      requiredField
      error={touched[`content${cardNum}`] && errors[`content${cardNum}`] ? errors[`content${cardNum}`] : ''}
    >
      <Form.Control 
        placeholder={`Enter card ${cardNum} content here...`}
        {...getFieldProps(`content${cardNum}`)}
        isInvalid={touched[`content${cardNum}`] && !!errors[`content${cardNum}`]}
      />
    </FormGroup>
  </>
);

export default function CardEdit() {
  const { id, partnerId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [data, setBusiness_card] = useState(
    Array.from({ length: 8 }, (_, i) => i + 1).reduce((acc, num) => {
      acc[`card_image${num}`] = "";
      acc[`content${num}`] = "";
      return acc;
    }, { index: null })
  );
  
  // Create refs array properly at component level
  const inputRefs = [
    useRef(null), useRef(null), useRef(null), useRef(null),
    useRef(null), useRef(null), useRef(null), useRef(null)
  ];
  
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const getInitialValues = () => 
    Array.from({ length: 8 }, (_, i) => i + 1).reduce((acc, num) => {
      acc[`card_image${num}`] = data[`card_image${num}`] || "";
      acc[`content${num}`] = data[`content${num}`] || "";
      acc[`old_card_image${num}`] = data[`card_image${num}`] || "";
      return acc;
    }, {});

  const fetchCardData = async () => {
    setIsLoading(true);
    if (!Number(id)) {
      setIsLoading(false);
      return;
    }

    try {
      const res = await getForBusinessCard(id);
      const CardData = res.data.data[0];
      setBusiness_card({
        ...CardData,
        index: res.data.data.indexOf(CardData),
      });
    } catch (error) {
      showToast({
        type: 'error',
        message: error?.response?.data?.message || "Failed to fetch card data. Please try again.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCardData();
  }, [id]);

  const handleSubmit = async (values, { setFieldValue }) => {
    setSaving(true);
    const formData = new FormData();
    
    // Append all content and images
    Array.from({ length: 8 }, (_, i) => i + 1).forEach(num => {
      formData.append(`content${num}`, values[`content${num}`]);
      if (values[`card_image${num}`] instanceof File) {
        formData.append(`card_image${num}`, values[`card_image${num}`]);
      }
    });

    try {
      if (!Number(id)) {
        formData.append("partner_id", partnerId);
        const res = await createForBusinessCard(formData);
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || "Cards section has been created successfully!",
          });
          const cardData = res.data.data;
          setBusiness_card(cardData);
          if(cardData?.id) {
            navigate(`/${partnerId}/forbusiness/card/edit/${cardData.id}`);
          }
        }
      } else {
        const res = await updateForBusinessCard(id, formData);
        if (res.status === 200) {
          showToast({
            type: 'success',
            message: res?.data?.message || "Cards section has been updated successfully!",
          });
          
          const updatedCardData = res?.data?.data;
          if (updatedCardData) {
            Array.from({ length: 8 }, (_, i) => i + 1).forEach(num => {
              if (updatedCardData[`card_image${num}`]) {
                setFieldValue(`card_image${num}`, updatedCardData[`card_image${num}`]);
                setFieldValue(`old_card_image${num}`, updatedCardData[`card_image${num}`]);
              }
              if (updatedCardData[`content${num}`]) {
                setFieldValue(`content${num}`, updatedCardData[`content${num}`]);
              }
            });
            
            setBusiness_card(prev => ({ ...prev, ...updatedCardData }));
            inputRefs.forEach(ref => {
              if (ref.current) ref.current.value = '';
            });
          }
        }
      }
    } catch (err) {
      showToast({
        type: 'error',
        message: err?.response?.data?.message || "Failed to update cards section. Please try again.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {isLoading ? <CmsCircularProgress/> : 
        <FormWrapper>
          <Formik
            initialValues={getInitialValues()}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
          >
            {({ values, errors, touched, handleSubmit, setFieldValue, isSubmitting, getFieldProps }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Title>{Number(id) ? 'Edit' : 'Add'} Cards Section</Title>
                <SliderDiv>
                  {Array.from({ length: 8 }, (_, i) => i + 1).map(cardNum => (
                    <CardSection
                      key={cardNum}
                      cardNum={cardNum}
                      values={values}
                      errors={errors}
                      touched={touched}
                      setFieldValue={setFieldValue}
                      inputRef={inputRefs[cardNum - 1]}
                      getFieldProps={getFieldProps}
                    />
                  ))}
                  <ButtonWrapper className="mt-3">
                    <Link to={`/${partnerId}/forbusiness/card/view/${Number(id) ? id : 'create'}`}>
                      <Button variant="secondary" className="w-100">Go Back</Button>
                    </Link>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={saving || isSubmitting}
                      className="w-100"
                    >
                      {saving ? "Saving..." : "Save Changes"}
                    </Button>
                  </ButtonWrapper>
                </SliderDiv>
              </Form>
            )}
          </Formik>
        </FormWrapper>
      }
    </>
  );
}

// Styled components
const ImageWrapper = styled.div`
  position: relative;
  max-width: 15vw;
  @media (max-width:576px) {
    max-width: 40vw;
  }
`;

const SliderDiv = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const FormWrapper = styled.div`
  @media (min-width:576px) {
    padding: 1rem;
  }
  @media (min-width:768px) {
    padding: 1.5rem;
  }
  padding: .5rem;
`;

const Title = styled.h3`
  text-align: center;
  padding: 10px 0;
`;

const SubTitle = styled.h5``;

const ButtonWrapper = styled.div`
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  @media (max-width: 890px) {
    gap: 2rem;
  }
  @media (max-width: 425px) {
    gap: 1rem;
  }
`;

const RemoveButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  position: absolute;
  top: -17px;
  right: -14px;
  @media (max-width: 576px) {
    top: -9px;
    right: -17px;
  }
`;

const UploadImage = styled.div`
  max-width: 15vw;
  overflow: hidden;
  border-radius: 6px;
  @media (max-width:576px) {
    max-width: 40vw;
  }
`;

const BootstrapImage = styled('img')(({ theme }) => ({
  maxWidth: "15vw",
  cursor: "pointer",
  "@media (max-width: 576px)": {
    maxWidth: "40vw",
  },
}));

const BootstrapButton = styled('button')(({ theme }) => ({
  margin: '0 !important',
  backgroundColor:'#FFFFFF',
  padding: '5px 7px',
  border: '1px solid #dc3545',
  "&:hover": {
    background: '#fffeff',
    boxShadow:'rgba(0, 0, 0, 0.2) 0px 2px 4px -1px, rgba(0, 0, 0, 0.14) 0px 4px 5px 0px, rgba(0, 0, 0, 0.12) 0px 1px 10px 0px'
  },
  '@media (max-width:576px)': {
    padding: '2px 4px',
  },
}));